import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye } from 'lucide-react'
import { counselingAdminApi } from '../../services/api'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Button from '../../components/ui/Button'
import Card from '../../components/ui/Card'
import { formatDate, formatDateTime } from '../../utils/date'
import { useMarkNotificationsRead } from '../../hooks/useMarkNotificationsRead'

const STATUSES = ['new', 'reviewed', 'scheduled', 'completed', 'declined']

export default function AdminCounseling() {
  const { t } = useTranslation()
  useMarkNotificationsRead('forms_counseling')
  const [items, setItems] = useState([])
  const [meta, setMeta] = useState(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)

  const load = (pageNum = page) => {
    setLoading(true)
    counselingAdminApi
      .getAllAdmin({ page: pageNum, per_page: 15 })
      .then(({ data }) => {
        setItems(data.data || [])
        setMeta(data.meta || null)
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load(page)
  }, [page])

  const updateStatus = async (id, status) => {
    await counselingAdminApi.updateStatus(id, { status })
    load(page)
  }

  return (
    <div>
      {loading ? (
        <LoadingSpinner className="py-20" />
      ) : (
        <>
          <Card padding={false} className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-primary/5">
                <tr>
                  <th className="px-4 py-3 text-start">{t('admin.applications.student')}</th>
                  <th className="px-4 py-3 text-start">{t('admin.applications.parent')}</th>
                  <th className="px-4 py-3 text-start">{t('admin.counseling.topic')}</th>
                  <th className="px-4 py-3 text-start">{t('admin.table.status')}</th>
                  <th className="px-4 py-3 text-start">{t('admin.applications.date')}</th>
                  <th className="px-4 py-3 text-start">{t('admin.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-border/60 align-top">
                    <td className="px-4 py-3">{item.student_name}</td>
                    <td className="px-4 py-3">
                      <div>{item.parent_name}</div>
                      <div className="text-xs text-text/60">{item.email}</div>
                      <div className="text-xs text-text/60">{item.phone}</div>
                    </td>
                    <td className="px-4 py-3">
                      {item.topic}
                      <div className="text-xs text-text/60">
                        {formatDate(item.preferred_date)} {item.preferred_time}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={item.status}
                        onChange={(e) => updateStatus(item.id, e.target.value)}
                        className="rounded-lg border border-border px-2 py-1 text-xs"
                      >
                        {STATUSES.map((status) => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-xs text-text/60">
                      {item.created_at ? formatDateTime(item.created_at) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/cp-login/forms/counseling/${item.id}`}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-primary transition-colors hover:bg-primary/5"
                        aria-label={t('admin.forms.view')}
                        title={t('admin.forms.view')}
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {meta && meta.last_page > 1 && (
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button variant="outlineDark" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                {t('admin.pagination.prev')}
              </Button>
              <span className="text-sm text-text/60">{page} / {meta.last_page}</span>
              <Button variant="outlineDark" size="sm" disabled={page >= meta.last_page} onClick={() => setPage(page + 1)}>
                {t('admin.pagination.next')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
